import React from 'react';
import { cn } from '@/lib/utils';

interface PageContainerProps {
  children: React.ReactNode;
  className?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md', 
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full'
};

const paddingClasses = {
  none: '',
  sm: 'px-4',
  md: 'px-4 md:px-6',
  lg: 'px-4 md:px-6 lg:px-8'
};

/**
 * PageContainer - Standardized container component for consistent page layouts
 * 
 * Provides consistent max-width, centering, and responsive padding across all pages.
 * Integrates with StandardPageHeader and existing design system.
 * 
 * @param maxWidth - Maximum width of the container (default: '7xl' for 1280px)
 * @param padding - Responsive padding level (default: 'md' for standard spacing)
 * @param className - Additional CSS classes
 * @param children - Content to be contained
 */
export const PageContainer: React.FC<PageContainerProps> = ({
  children,
  className,
  maxWidth = '7xl',
  padding = 'md'
}) => {
  return (
    <div 
      className={cn(
        'mx-auto w-full',
        maxWidthClasses[maxWidth],
        paddingClasses[padding],
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * PageLayout - Complete page layout with StandardPageHeader integration
 * 
 * Combines StandardPageHeader with PageContainer for a complete page layout.
 * Use this for new pages or when refactoring existing pages.
 */
interface PageLayoutProps {
  children: React.ReactNode;
  className?: string;
  containerProps?: Omit<PageContainerProps, 'children'>;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  className,
  containerProps
}) => {
  return (
    <div className={cn('space-y-6', className)}>
      <PageContainer {...containerProps}>
        {children}
      </PageContainer>
    </div>
  );
};

export default PageContainer;